import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useDeployAuth } from "../../context/DeployAuthContext";
import LimeLogo from "../../assets/lime-logo-no-bg.png";
// Directory casing is "Arixa" — the prod build runs on Linux, where a lowercase
// path silently resolves on Windows and then fails the build.
import arixaIcon from "../../assets/Arixa/arixa-icon.png";
import DeployLegalFooter from "../../components/deploy/DeployLegalFooter";
import McpConnectTabs from "../../components/deploy/McpConnectTabs";
import CoreOnboardingCard from "../../components/limelite/CoreOnboardingCard";

const api = axios.create({ baseURL: "", withCredentials: true });

const CATEGORIES = ["saas","developer-tool","productivity","ecommerce","ai","mobile","other"];
const PLATFORMS  = ["web","android","ios","cross-platform"];

const STORE_STATUS_STYLES = {
  active:             "text-lime-300 border-lime-700 bg-lime-900/20",
  provisioning:       "text-amber-300 border-amber-700 bg-amber-900/20",
  provisioning_db:    "text-amber-300 border-amber-700 bg-amber-900/20",
  provisioning_infra: "text-amber-300 border-amber-700 bg-amber-900/20",
  reserved:           "text-blue-300 border-blue-700 bg-blue-900/20",
  pending:            "text-blue-300 border-blue-700 bg-blue-900/20",
  degraded:           "text-red-300 border-red-700 bg-red-900/20",
  suspended:          "text-silver-dark border-gray-700",
  failed:             "text-red-400 border-red-800",
};

const STORE_STATUS_LABELS = {
  active:             "Live",
  provisioning:       "Setting up",
  provisioning_db:    "Setting up database",
  provisioning_infra: "Setting up infrastructure",
  reserved:           "Reserved",
  pending:            "Setting up",
  degraded:           "Degraded",
  suspended:          "Suspended",
  failed:             "Setup failed",
};

function StoreStatusBadge({ status }) {
  const s = (status || "pending").toLowerCase();
  const label = STORE_STATUS_LABELS[s] || s.replace(/_/g, " ");
  return (
    <span className={`text-xs border rounded px-2 py-0.5 capitalize ${STORE_STATUS_STYLES[s] || STORE_STATUS_STYLES.pending}`}>
      {label}
    </span>
  );
}

function SubStatusBadge({ status }) {
  if (!status) return null;
  const s = status.toLowerCase();
  const styles =
    s === "active" || s === "trialing"
      ? "text-lime-300 border-lime-700 bg-lime-900/20"
      : s === "past_due" || s === "unpaid"
      ? "text-red-300 border-red-700 bg-red-900/20"
      : "text-silver-dark border-gray-700 bg-gray-900/20";
  const label = s === "active" ? "Active" : s === "trialing" ? "Trialing" : s === "past_due" ? "Past Due" : s === "unpaid" ? "Unpaid" : status;
  return (
    <span className={`text-xs uppercase tracking-wider border rounded px-2 py-0.5 ${styles}`}>
      {label}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch { return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="text-xs border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-2 py-0.5 transition-colors flex-shrink-0">
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function SectionCard({ title, tag, children }) {
  return (
    <div className="bg-off-black border border-gray-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-sm uppercase tracking-widest text-silver-dark">{title}</h2>
        {tag && <span className="text-xs text-lime-500 uppercase tracking-widest">{tag}</span>}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-800 last:border-0">
      <span className="text-silver-dark text-sm w-36 flex-shrink-0">{label}</span>
      <span className="text-silver-light text-sm text-right flex-1 flex items-center justify-end gap-2 flex-wrap">
        {children}
      </span>
    </div>
  );
}

function formatEur(cents) {
  if (cents == null) return "—";
  return `€${(cents / 100).toFixed(2)}`;
}

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(typeof ts === "number" ? ts * 1000 : ts).toLocaleDateString("en-GB", {
    year: "numeric", month: "short", day: "numeric",
  });
}

/**
 * Arixa premium is included with every Limelite plan, and until this card existed
 * nothing told the customer so — it was granted silently, so most would never use
 * it. Two states matter:
 *
 *   active  — premium is live on their Arixa account; nudge them to go use it.
 *   pending — the grant is waiting for them to sign up. This case is why the card
 *             leads with the email address: the entitlement is keyed to it, so
 *             registering on Arixa with any other address silently misses it.
 *
 * Renders nothing at all unless the backend says `show` — an entitlement that has
 * not actually landed yet must not be advertised as a perk they can use.
 */
const ARIXA_DISMISS_KEY = "limelite.arixaNudgeDismissed";

// Arixa's own brand, not Limelite's: violet/fuchsia on deep purple-black, per its
// manifest theme_color #8B5CF6. Deliberately NOT the lime used everywhere else on
// this page — the card is a different product's premium tier, and reading as a
// guest brand is what stops it looking like an inline notice.
const ARIXA_PERKS = ["Ad-free listening", "Aria AI assistant", "High-quality streaming"];

function ArixaCardShell({ children, onDismiss }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-950/50 via-off-black to-black">
      {/* Soft brand glow. aria-hidden + pointer-events-none: pure decoration. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-violet-600/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-10 -left-24 h-40 w-40 rounded-full bg-fuchsia-600/10 blur-3xl"
      />
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute right-3 top-3 z-10 rounded p-1 text-lg leading-none text-silver-dark transition-colors hover:text-silver-light"
          aria-label="Dismiss Arixa premium reminder"
        >
          ×
        </button>
      )}
      <div className="relative p-6">{children}</div>
    </div>
  );
}

function ArixaCardHeader({ via }) {
  return (
    <div className="mb-4 flex items-center gap-4">
      <img
        src={arixaIcon}
        alt="Arixa"
        width="48"
        height="48"
        className="h-12 w-12 flex-shrink-0 rounded-xl ring-1 ring-violet-400/30 shadow-[0_0_24px_rgba(139,92,246,0.35)]"
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="font-serif text-xl leading-none text-silver-light">Arixa Premium</h2>
          <span className="rounded-full border border-violet-400/40 bg-violet-500/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-300">
            Included
          </span>
        </div>
        <p className="mt-1.5 truncate text-xs text-silver-dark">Included with {via} — no extra cost</p>
      </div>
    </div>
  );
}

function ArixaPerks() {
  return (
    <div className="mb-5 flex flex-wrap gap-2">
      {ARIXA_PERKS.map((perk) => (
        <span
          key={perk}
          className="rounded-full border border-violet-500/20 bg-violet-500/5 px-2.5 py-1 text-xs text-silver-medium"
        >
          {perk}
        </span>
      ))}
    </div>
  );
}

function ArixaPremiumCard({ data }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(ARIXA_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (!data || !data.show) return null;
  // Only the "go enjoy it" nudge is dismissible. The pending card is an action
  // the customer still has to take, so it stays put until they take it.
  if (data.status === "active" && dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(ARIXA_DISMISS_KEY, "1");
    } catch {
      /* private mode — dismiss for this page view only */
    }
  };

  const via = Array.isArray(data.via) && data.via.length ? data.via.join(" + ") : "your Limelite plan";

  if (data.status === "pending") {
    return (
      <ArixaCardShell>
        <ArixaCardHeader via={via} />
        <p className="mb-4 text-sm text-silver-light">
          Your premium subscription is <span className="text-violet-300">ready and waiting</span> — it
          just needs an Arixa account.
        </p>
        <ArixaPerks />
        {/* The address the grant is keyed to. Given its own framed block because it
            is the one thing on this card that must not be skimmed past: signing up
            with a different email silently misses the entitlement. */}
        <div className="mb-5 rounded-lg border border-violet-500/20 bg-black/40 p-3">
          <p className="mb-1 text-[11px] uppercase tracking-widest text-silver-dark">Sign up with this email</p>
          <p className="break-all font-medium text-silver-light">{data.email}</p>
          <p className="mt-1.5 text-xs text-silver-dark">
            Premium activates automatically. A different address won't pick it up.
          </p>
        </div>
        <a
          href={data.signupUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-violet-900/40 transition-all hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-800/50"
        >
          Claim your premium
          <span aria-hidden="true">→</span>
        </a>
      </ArixaCardShell>
    );
  }

  return (
    <ArixaCardShell onDismiss={dismiss}>
      <ArixaCardHeader via={via} />
      <p className="mb-4 text-sm text-silver-light">
        Remember to take advantage of your Arixa premium — it's{" "}
        <span className="text-violet-300">active and ready to use</span>.
      </p>
      <ArixaPerks />
      <a
        href={data.appUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-lg border border-violet-500/40 bg-violet-500/10 px-4 py-2.5 text-sm font-medium text-violet-200 transition-colors hover:border-violet-400/70 hover:bg-violet-500/20 hover:text-white"
      >
        Open Arixa
        <span aria-hidden="true">→</span>
      </a>
    </ArixaCardShell>
  );
}

function ListingForm({ onSuccess }) {
  const [form, setForm] = useState({ name:"", tagline:"", description:"", appUrl:"", category:"saas", platform:"web", apkUrl:"", tags:"", techStack:"", iconUrl:"" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const res = await api.post("/api/marketplace/manage/listings", {
        ...form,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        techStack: form.techStack.split(",").map(t => t.trim()).filter(Boolean),
      });
      onSuccess(res.data.listing);
    } catch (ex) {
      setErr(ex.response?.data?.error || "Failed to create listing");
    } finally { setBusy(false); }
  };
  const input = "w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light placeholder-silver-dark focus:outline-none focus:border-gray-500";
  return (
    <form onSubmit={submit} className="mt-5 space-y-3 border-t border-gray-800 pt-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-silver-dark block mb-1">App name *</label><input className={input} required value={form.name} onChange={e=>set("name",e.target.value)} placeholder="My App" /></div>
        <div><label className="text-xs text-silver-dark block mb-1">Category</label>
          <select className={input} value={form.category} onChange={e=>set("category",e.target.value)}>
            {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div><label className="text-xs text-silver-dark block mb-1">Tagline * (160 chars)</label><input className={input} required value={form.tagline} onChange={e=>set("tagline",e.target.value)} placeholder="One sentence about your app" maxLength={160} /></div>
      <div><label className="text-xs text-silver-dark block mb-1">Description</label><textarea className={`${input} h-20 resize-none`} value={form.description} onChange={e=>set("description",e.target.value)} placeholder="Tell people what your app does..." /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-silver-dark block mb-1">Platform</label>
          <select className={input} value={form.platform} onChange={e=>set("platform",e.target.value)}>
            {PLATFORMS.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-silver-dark block mb-1">App URL</label><input className={input} value={form.appUrl} onChange={e=>set("appUrl",e.target.value)} placeholder="https://myapp.com" /></div>
      </div>
      {(form.platform==="android"||form.platform==="cross-platform") && (
        <div><label className="text-xs text-silver-dark block mb-1">APK download URL</label><input className={input} value={form.apkUrl} onChange={e=>set("apkUrl",e.target.value)} placeholder="https://..." /></div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-silver-dark block mb-1">Icon URL</label><input className={input} value={form.iconUrl} onChange={e=>set("iconUrl",e.target.value)} placeholder="https://..." /></div>
        <div><label className="text-xs text-silver-dark block mb-1">Tags (comma-separated)</label><input className={input} value={form.tags} onChange={e=>set("tags",e.target.value)} placeholder="open-source, free, tool" /></div>
      </div>
      {err && <p className="text-red-400 text-xs">{err}</p>}
      <button type="submit" disabled={busy} className="bg-lime-600 hover:bg-lime-500 text-black font-bold text-sm py-2 px-5 rounded-lg transition-colors disabled:opacity-50">
        {busy ? "Publishing…" : "Publish listing"}
      </button>
    </form>
  );
}

export default function LimeliteAccountPage() {
  const { customer, logout } = useDeployAuth();

  // Deploy data
  const [subscription, setSubscription] = useState(null);
  const [spend, setSpend] = useState(null);
  const [keys, setKeys] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [deployLoading, setDeployLoading] = useState(true);
  const [deployError, setDeployError] = useState("");

  // Storefront data
  // Bundled Arixa premium. Failure is silent on purpose — a perk nudge must never
  // surface an error banner on the account page.
  const [arixa, setArixa] = useState(null);

  const [sfSubscription, setSfSubscription] = useState(null);
  const [sfProject, setSfProject] = useState(null);
  const [sfLoading, setSfLoading] = useState(true);
  const [sfError, setSfError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  // Marketplace boost
  const [offer, setOffer] = useState(null);
  const [boostingKey, setBoostingKey] = useState("");
  const [boostMsg, setBoostMsg] = useState(null);
  const boostRef = useRef(null);
  const [highlightBoost, setHighlightBoost] = useState(false);

  // Marketplace listing form
  const [showListingForm, setShowListingForm] = useState(false);
  const [listingSuccess, setListingSuccess] = useState("");

  // GDPR
  const [gdprExporting, setGdprExporting] = useState(false);
  const [gdprDeleteStep, setGdprDeleteStep] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  const loadDeploy = useCallback(async () => {
    setDeployLoading(true);
    setDeployError("");
    try {
      const [subRes, spendRes, keysRes, invRes, currentPeriodRes] = await Promise.all([
        api.get("/api/deploy/billing/subscription"),
        api.get("/api/deploy/billing/spend"),
        api.get("/api/deploy/keys"),
        api.get("/api/deploy/billing/invoices").catch(() => ({ data: { invoices: [] } })),
        api.get("/api/deploy/billing/current-period").catch(() => ({ data: { period: null } })),
      ]);
      setSubscription(subRes.data.subscription || null);
      setSpend(spendRes.data || null);
      setKeys(keysRes.data.keys || []);
      setInvoices(invRes.data.invoices || []);
      setCurrentPeriod(currentPeriodRes.data.period || null);
    } catch (e) {
      setDeployError(e.response?.data?.error || e.message || "Failed to load account data");
    } finally {
      setDeployLoading(false);
    }
  }, []);

  const loadArixa = useCallback(async () => {
    try {
      const { data: d } = await api.get("/api/arixa/entitlement");
      setArixa(d || null);
    } catch {
      // Swallowed deliberately: this is an optional nudge, not account data. If
      // the lookup fails the card simply does not render.
      setArixa(null);
    }
  }, []);

  const loadStorefront = useCallback(async () => {
    setSfLoading(true);
    setSfError("");
    try {
      const { data: d } = await api.get("/api/storefront/billing/subscription");
      setSfSubscription(d.subscription || null);
      setSfProject(d.project || null);
    } catch (e) {
      setSfError(e.response?.data?.error || "Failed to load storefront data");
    } finally {
      setSfLoading(false);
    }
  }, []);

  const loadOffer = useCallback(async () => {
    try {
      const { data: d } = await api.get("/api/marketplace/manage/boost-offer");
      setOffer(d);
    } catch {
      // Non-fatal
    }
  }, []);

  useEffect(() => {
    loadDeploy();
    loadStorefront();
    loadOffer();
    loadArixa();
  }, [loadDeploy, loadStorefront, loadOffer, loadArixa]);

  // Deep-link boost scroll
  useEffect(() => {
    if (!offer?.listings?.length) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("boost") === "1" && boostRef.current) {
      boostRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightBoost(true);
      const t = setTimeout(() => setHighlightBoost(false), 2400);
      return () => clearTimeout(t);
    }
  }, [offer]);

  const boost = async (listingId, tier) => {
    setBoostMsg(null);
    setBoostingKey(`${listingId}:${tier}`);
    try {
      await api.post(`/api/marketplace/manage/listings/${listingId}/boost`, { tier });
      setBoostMsg({ ok: true, text: "Boost activated — your store is now featured on the marketplace." });
      await loadOffer();
    } catch (e) {
      const d = e.response?.data;
      setBoostMsg({ ok: false, text: d?.error || "Could not start the boost. Please try again." });
    } finally {
      setBoostingKey("");
    }
  };

  const openStorefrontPortal = async () => {
    setPortalLoading(true);
    try {
      const { data: d } = await api.post("/api/storefront/billing/create-portal-session");
      if (d.url) window.location.href = d.url;
    } catch (e) {
      setSfError(e.response?.data?.error || "Could not open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleGdprExport = async (format) => {
    setGdprExporting(true);
    try {
      if (format === "zip") {
        const a = document.createElement("a");
        a.href = "/api/deploy/privacy/data-export.zip";
        a.download = "limelite-data-export.zip";
        a.click();
      } else {
        const res = await api.get("/api/deploy/privacy/data-export");
        const blob = new Blob([JSON.stringify(res.data.export, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "limelite-data-export.json";
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      setDeployError(e.response?.data?.error || "Export failed — try again later.");
    } finally {
      setGdprExporting(false);
    }
  };

  const handleDeleteRequest = async () => {
    setDeleteError("");
    setDeleteLoading(true);
    try {
      await api.post("/api/deploy/privacy/deletion-request", {
        password: deletePassword,
        confirmPhrase: deletePhrase,
      });
      setDeleteConfirmed(true);
      logout();
    } catch (e) {
      setDeleteError(e.response?.data?.error || "Could not submit deletion request.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const firstKey = keys.find((k) => !k.revokedAt);
  const mtd = spend?.currentMonthMtdEur ?? spend?.settings?.currentMonthMtdEur ?? null;
  const budget = spend?.settings?.monthlyBudgetEur ?? null;
  const spendPct = budget && mtd != null ? Math.min((mtd / budget) * 100, 100) : null;

  const sfProjectStatus = sfProject?.status || "pending";
  const sfSubActive = sfSubscription && ["active", "trialing"].includes((sfSubscription.status || "").toLowerCase());

  if (deployLoading && sfLoading) {
    return (
      <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 py-12">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src={LimeLogo} alt="Limelite" className="w-8 h-8" />
            <div>
              <p className="text-xs uppercase tracking-widest text-lime-500">Limelite</p>
              <h1 className="text-2xl font-serif font-medium">Your Account</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/limelite/deploy/dashboard" className="text-sm text-silver-dark hover:text-silver-light transition-colors">
              Dashboard →
            </Link>
            <button onClick={logout} className="text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors">
              Sign out
            </button>
          </div>
        </div>

        <div className="space-y-6">

          {/* Account Details */}
          <SectionCard title="Account Details">
            <Row label="Email">{customer?.email || "—"}</Row>
            {customer?.company && <Row label="Company">{customer.company}</Row>}
            <Row label="Email Verified">
              {customer?.emailVerified
                ? <span className="text-lime-400">Verified</span>
                : <span className="text-amber-400">Not verified — check your inbox</span>}
            </Row>
          </SectionCard>

          {/* Bundled Arixa premium — renders only when there is actually one to use */}
          <ArixaPremiumCard data={arixa} />

          {/* ── Legacy Storefront — existing subscribers only (V1 consolidation: the
                 standalone Storefront product is retired; Account must not sell it).
                 Customers without a storefront subscription see nothing here. ── */}
          {sfSubscription && (
            <>
          <div className="border-t border-gray-800 pt-2">
            <p className="text-xs uppercase tracking-widest text-lime-600 mb-4">Your Store <span className="text-silver-dark normal-case tracking-normal">(legacy Storefront plan)</span></p>
          </div>

          {sfError && (
            <div className="text-red-400 text-sm border border-red-900 rounded-lg p-3 bg-red-900/10">{sfError}</div>
          )}

          <SectionCard title="Store">
              <>
                <Row label="Status"><StoreStatusBadge status={sfProjectStatus} /></Row>
                {sfProject?.statusMessage && (
                  <Row label="Info"><span className="text-silver-dark text-xs">{sfProject.statusMessage}</span></Row>
                )}
                {sfProject?.storeUrl ? (
                  <Row label="Store URL">
                    <a href={sfProject.storeUrl} target="_blank" rel="noopener noreferrer" className="text-lime-400 hover:text-lime-300 transition-colors text-xs font-mono truncate max-w-xs">
                      {sfProject.storeUrl}
                    </a>
                    <CopyButton text={sfProject.storeUrl} />
                  </Row>
                ) : (
                  <Row label="Store URL">
                    <span className="text-silver-dark text-xs italic">
                      {["pending","provisioning_db","provisioning_infra","provisioning"].includes(sfProjectStatus)
                        ? "Being provisioned — check back in ~15 minutes"
                        : "Not yet assigned"}
                    </span>
                  </Row>
                )}
                {sfProject?.adminUrl && (
                  <Row label="Admin panel">
                    <a href={sfProject.adminUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-lime-600 hover:bg-lime-500 text-black font-semibold text-xs py-1.5 px-3 rounded transition-colors">
                      Open admin →
                    </a>
                    <CopyButton text={sfProject.adminUrl} />
                  </Row>
                )}
                {sfProject?.publicIpAddress && (
                  <Row label="IP">
                    <code className="text-silver-medium font-mono text-xs">{sfProject.publicIpAddress}</code>
                    <CopyButton text={sfProject.publicIpAddress} />
                  </Row>
                )}
              </>
          </SectionCard>

          {sfProject && sfProjectStatus === "pending" && (
            <div className="bg-blue-900/10 border border-blue-800/30 rounded-xl p-5">
              <p className="text-sm text-blue-300 font-medium mb-2">Your store is being set up</p>
              <p className="text-silver-dark text-sm leading-relaxed">
                We're deploying the Limelite Storefront to your AWS instance. This usually takes 3–5 minutes.
                Once ready, your store URL and admin panel link will appear above.
                You'll also receive an email with your admin credentials.
              </p>
            </div>
          )}
            </>
          )}

          {/* Marketplace boost upsell */}
          {offer?.listings?.length > 0 && (
            <div
              ref={boostRef}
              className={`bg-off-black border rounded-xl p-6 transition-all duration-500 ${highlightBoost ? "border-lime-500 ring-1 ring-lime-600/40" : "border-gray-800"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm uppercase tracking-widest text-silver-dark">Marketplace boost</h2>
                <span className="text-[11px] text-lime-500 uppercase tracking-widest">Get discovered</span>
              </div>
              <p className="text-silver-dark text-sm mb-5">
                Feature your store on the Limelite Marketplace and put it in front of shoppers browsing for products like yours.
              </p>
              {boostMsg && (
                <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${boostMsg.ok ? "bg-lime-900/10 border-lime-900/40 text-lime-300" : "bg-red-900/10 border-red-900/40 text-red-400"}`}>
                  {boostMsg.text}
                </div>
              )}
              {offer.listings.map((listing) => (
                <div key={listing.id} className="border-b border-gray-800 last:border-0 py-4 first:pt-0">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-silver-light text-sm font-medium truncate">{listing.name}</p>
                      <p className="text-silver-dark text-xs mt-0.5">
                        {listing.viewCount} {listing.viewCount === 1 ? "view" : "views"}
                        {listing.sourceType === "storefront" ? " · Store" : ""}
                      </p>
                    </div>
                    {listing.featured && (
                      <span className="text-xs border rounded px-2 py-0.5 text-lime-300 border-lime-700 bg-lime-900/20 capitalize flex-shrink-0">
                        {listing.featuredTier} · until {new Date(listing.featuredUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {offer.tiers.map((t) => {
                      const busy = boostingKey === `${listing.id}:${t.id}`;
                      return (
                        <button
                          key={t.id}
                          onClick={() => boost(listing.id, t.id)}
                          disabled={Boolean(boostingKey)}
                          className="text-sm font-semibold bg-lime-600 hover:bg-lime-500 text-black rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
                        >
                          {busy ? "Processing…" : `${listing.featured ? "Extend" : "Boost"} — ${t.label} ${t.priceLabel}/${t.days}d`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {!offer.hasBillingAccount && (
                <p className="mt-4 text-xs text-amber-400">You'll be asked to add a payment method the first time you boost.</p>
              )}
            </div>
          )}

          {sfSubActive && (
            <SectionCard title="Storefront Subscription (legacy)">
              <Row label="Plan">Limelite Storefront — legacy</Row>
              <Row label="Status"><SubStatusBadge status={sfSubscription.status} /></Row>
              {sfSubscription.currentPeriodEnd && (
                <Row label="Renews">
                  {new Date(sfSubscription.currentPeriodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </Row>
              )}
              <Row label="Price">€49 / month — legacy plan, includes platform access</Row>
              <div className="mt-5">
                <button
                  onClick={openStorefrontPortal}
                  disabled={portalLoading}
                  className="text-sm border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-3 py-1.5 transition-colors disabled:opacity-50"
                >
                  {portalLoading ? "Loading…" : "Manage billing →"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* ── Platform: subscription, projects, usage, MCP, billing (contract §3) ── */}
          <div className="border-t border-gray-800 pt-2">
            <p className="text-xs uppercase tracking-widest text-lime-600 mb-4">Platform</p>
          </div>

          {deployError && (
            <div className="text-red-400 text-sm border border-red-900 rounded-lg p-3 bg-red-900/10">{deployError}</div>
          )}

          <SectionCard title="Subscription">
            <Row label="Product">
              <span className="flex items-center gap-2">
                Limelite
                {subscription?.planTier === "enterprise" && (
                  <span className="text-xs border border-amber-700 text-amber-300 rounded px-1.5 py-0.5">Enterprise</span>
                )}
              </span>
            </Row>
            <Row label="Status">
              {subscription
                ? <SubStatusBadge status={subscription.status} />
                : <span className="text-silver-dark">No active subscription</span>}
            </Row>
            {subscription?.currentPeriodEnd && (
              <Row label="Renews">{formatDate(subscription.currentPeriodEnd)}</Row>
            )}
            <Row label="Base price">€29 / month + metered usage</Row>
            {!subscription && (
              <div className="mt-4">
                <Link to="/limelite/deploy/dashboard" className="inline-flex items-center bg-lime-600 hover:bg-lime-500 text-black font-semibold text-sm py-2 px-4 rounded-lg transition-colors">
                  Subscribe now →
                </Link>
              </div>
            )}
          </SectionCard>

          <CoreOnboardingCard />

          {subscription && (
            <SectionCard title="Usage This Month">
              {currentPeriod && (
                <div className="mb-4 rounded-lg border border-gray-800 bg-gray-900/40 p-3 text-sm">
                  <div className="flex justify-between gap-3"><span className="text-silver-dark">Included infrastructure allowance</span><span className="text-silver-light">€{currentPeriod.includedInfraAllowanceEur.toFixed(2)}</span></div>
                  <div className="flex justify-between gap-3 mt-1"><span className="text-silver-dark">Used so far</span><span className="text-silver-light">€{currentPeriod.infraUsedEur.toFixed(2)} <span className="text-xs text-silver-dark">({currentPeriod.status})</span></span></div>
                  <div className="flex justify-between gap-3 mt-1"><span className="text-silver-dark">Projected usage charge</span><span className="text-silver-light">€{currentPeriod.projectedOverageEur.toFixed(2)}</span></div>
                  <p className="text-xs text-silver-dark mt-2">Projected from {currentPeriod.source}; your finalized invoice keeps its own immutable usage record.</p>
                  {currentPeriod.aiMeter && (
                    <div className="mt-3 border-t border-gray-800 pt-3">
                      <div className="flex justify-between gap-3"><span className="text-silver-dark">AI token usage</span><span className="text-silver-light">{Number(currentPeriod.aiMeter.callCount || 0).toLocaleString()} calls</span></div>
                      <div className="flex justify-between gap-3 mt-1"><span className="text-silver-dark">AI provider cost to date</span><span className="text-silver-light">{currentPeriod.aiMeter.costStatus === "unavailable" ? "Unavailable" : `€${Number(currentPeriod.aiMeter.providerCostEur || 0).toFixed(2)}`}</span></div>
                      <p className="text-xs text-silver-dark mt-2">AI is token-metered separately from AWS infrastructure and is not included in the projected infrastructure usage charge.{currentPeriod.aiMeter.costStatus === "unavailable" ? ` ${currentPeriod.aiMeter.evidenceError || "AI token-meter evidence is temporarily unavailable."}` : currentPeriod.aiMeter.costStatus !== "complete" ? " Some AI models still need pricing verification." : ""}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="mb-4">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-2xl font-mono font-medium text-silver-light">
                    {mtd != null ? `€${mtd.toFixed(2)}` : "—"}
                  </span>
                  {budget != null && (
                    <span className="text-sm text-silver-dark">of €{budget.toFixed(2)} budget</span>
                  )}
                </div>
                {spendPct != null && (
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${spendPct > 85 ? "bg-red-500" : spendPct > 60 ? "bg-amber-500" : "bg-lime-500"}`}
                      style={{ width: `${spendPct}%` }}
                    />
                  </div>
                )}
              </div>
              {subscription?.currentPeriodEnd && (
                <p className="text-xs text-silver-dark">Resets {formatDate(subscription.currentPeriodEnd)}</p>
              )}
              <p className="text-xs text-silver-dark mt-1">€29 platform + infrastructure allowance, then metered overage. A cap cannot be silently exceeded.</p>
            </SectionCard>
          )}

          <SectionCard title="MCP Integration">
            {firstKey ? (
              <>
                <Row label="API Key">
                  <code className="font-mono text-xs text-silver-light bg-gray-900 rounded px-2 py-1">
                    {firstKey.keyPreview || "••••••••••••••••"}
                  </code>
                  <Link to="/limelite/deploy/dashboard" className="text-xs text-silver-dark hover:text-silver-light transition-colors">
                    Manage keys →
                  </Link>
                </Row>
                <div className="mt-4">
                  <p className="text-xs text-silver-dark mb-2">Connect to your AI tool</p>
                  <McpConnectTabs />
                  <>
                    <p className="text-xs text-amber-400/80 mt-2">
                      Open the{" "}
                      <Link to="/limelite/deploy/dashboard" className="underline hover:text-amber-300">dashboard</Link>{" "}
                      to create a key. Its full value is shown once and is never kept in this browser.
                    </p>
                  </>
                </div>
                <p className="text-xs text-silver-dark mt-3">
                  New keys can be project-scoped. Account-wide keys are compatibility-only and should be rotated.
                  Works with Claude Code, Cursor, Windsurf, and any MCP-compatible IDE.{" "}
                  <Link to="/limelite/deploy/setup" className="text-lime-600 hover:text-lime-400 transition-colors">Setup guide →</Link>
                </p>
              </>
            ) : (
              <p className="text-silver-dark text-sm">
                No API keys yet.{" "}
                <Link to="/limelite/deploy/dashboard" className="text-lime-600 hover:text-lime-400">Create one in the dashboard →</Link>
              </p>
            )}
          </SectionCard>

          <SectionCard title="Invoices">
            {invoices.length === 0 ? (
              <p className="text-silver-dark text-sm">No invoices yet.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-silver-dark text-xs uppercase tracking-wider border-b border-gray-800">
                        <th className="text-left pb-3 font-medium">Invoice</th>
                        <th className="text-left pb-3 font-medium">Date</th>
                        <th className="text-left pb-3 font-medium">Amount</th>
                        <th className="text-left pb-3 font-medium">Status</th>
                        <th className="text-right pb-3 font-medium">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-gray-900 hover:bg-gray-900/30 transition-colors">
                          <td className="py-3 text-silver-medium font-mono text-xs">{inv.number || inv.id}</td>
                          <td className="py-3 text-silver-medium">{formatDate(inv.created)}</td>
                          <td className="py-3 text-silver-light font-medium">
                            {formatEur(inv.amount_paid || inv.amount_due)}{" "}
                            <span className="text-silver-dark text-xs">{(inv.currency || "eur").toUpperCase()}</span>
                          </td>
                          <td className="py-3"><SubStatusBadge status={inv.status} /></td>
                          <td className="py-3 text-right">
                            {inv.invoice_pdf ? (
                              <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-xs text-lime-600 hover:text-lime-400 transition-colors">
                                Download PDF
                              </a>
                            ) : (
                              <span className="text-silver-dark text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-5 flex gap-3">
                  <a href="/api/deploy/billing/invoices/export.csv" download className="inline-flex items-center text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors">
                    Export CSV
                  </a>
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.post("/api/deploy/billing/create-portal-session");
                        if (res.data.url) window.location.href = res.data.url;
                      } catch {
                        setDeployError("Could not open billing portal — try again.");
                      }
                    }}
                    className="inline-flex items-center text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors"
                  >
                    Manage billing →
                  </button>
                </div>
              </>
            )}
          </SectionCard>

          {/* ── Marketplace ── */}
          <div className="border-t border-gray-800 pt-2">
            <p className="text-xs uppercase tracking-widest text-lime-600 mb-4">Marketplace</p>
          </div>

          <SectionCard title="List your app">
            <p className="text-silver-dark text-sm mb-5">
              List any web app, SaaS tool, or Android APK on the Limelite Marketplace. Free with your subscription.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/limelite/marketplace" className="text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors">
                Browse marketplace →
              </Link>
              <button
                onClick={() => setShowListingForm(v => !v)}
                className="text-sm text-lime-400 border border-lime-700 hover:border-lime-500 rounded px-3 py-1.5 transition-colors"
              >
                {showListingForm ? "Cancel" : "+ List your app"}
              </button>
            </div>
            {showListingForm && (
              <ListingForm
                onSuccess={(listing) => {
                  setShowListingForm(false);
                  setListingSuccess(`"${listing.name}" is now live on the marketplace.`);
                }}
              />
            )}
            {listingSuccess && (
              <p className="text-lime-400 text-sm mt-3">
                {listingSuccess}{" "}
                <Link to="/limelite/marketplace" className="underline">View it →</Link>
              </p>
            )}
          </SectionCard>

          {/* ── Privacy & Data ── */}
          <div className="border-t border-gray-800 pt-2">
            <p className="text-xs uppercase tracking-widest text-lime-600 mb-4">Privacy & Data</p>
          </div>

          <SectionCard title="Your data">
            <p className="text-silver-dark text-sm mb-5">
              Download a complete copy of your account data or request deletion. Governed by GDPR.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={() => handleGdprExport("json")}
                disabled={gdprExporting}
                className="text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                {gdprExporting ? "Exporting…" : "Export JSON"}
              </button>
              <button
                onClick={() => handleGdprExport("zip")}
                disabled={gdprExporting}
                className="text-sm text-silver-dark hover:text-silver-light border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                Export ZIP
              </button>
            </div>

            {!gdprDeleteStep && !deleteConfirmed && (
              <button onClick={() => setGdprDeleteStep(true)} className="text-sm text-red-400/70 hover:text-red-400 transition-colors">
                Request account deletion →
              </button>
            )}

            {gdprDeleteStep && !deleteConfirmed && (
              <div className="border border-red-900/50 rounded-lg p-5 bg-red-900/5 mt-2">
                <p className="text-sm text-silver-light mb-1 font-medium">Delete your account</p>
                <p className="text-xs text-silver-dark mb-4">
                  This cancels your subscription and schedules all data for deletion within 30 days. This cannot be undone.
                  Type <span className="font-mono text-silver-medium">delete my account</span> to confirm.
                </p>
                <div className="space-y-3">
                  <input type="password" placeholder="Your password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                    className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light placeholder-silver-dark focus:outline-none focus:border-gray-500" />
                  <input type="text" placeholder="delete my account" value={deletePhrase} onChange={(e) => setDeletePhrase(e.target.value)}
                    className="w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light placeholder-silver-dark focus:outline-none focus:border-gray-500" />
                  {deleteError && <p className="text-red-400 text-xs">{deleteError}</p>}
                  <div className="flex gap-3">
                    <button onClick={handleDeleteRequest} disabled={deleteLoading || !deletePassword || !deletePhrase}
                      className="text-sm text-red-400 border border-red-800 hover:border-red-600 rounded px-4 py-1.5 transition-colors disabled:opacity-40">
                      {deleteLoading ? "Submitting…" : "Confirm deletion"}
                    </button>
                    <button onClick={() => { setGdprDeleteStep(false); setDeletePassword(""); setDeletePhrase(""); setDeleteError(""); }}
                      className="text-sm text-silver-dark hover:text-silver-light transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {deleteConfirmed && (
              <p className="text-sm text-silver-medium mt-2">
                Deletion request received. You have been signed out. Your data will be removed within 30 days.
              </p>
            )}
          </SectionCard>

        </div>

        <div className="mt-10">
          <DeployLegalFooter />
        </div>
      </div>
    </div>
  );
}
